import {
  Autocomplete,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  InputAdornment,
  OutlinedInput,
  TextField,
  Typography,
} from '@mui/material';
import { DesktopDateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { useAsyncAction } from 'app/services';
import { inspectionFilterLoad } from 'app/store/checklist';
import moment from 'moment';
import React, { forwardRef, useEffect, useImperativeHandle } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';

const isVoykanUi = config.clientUi === 'voykan';
const inspectionStatusFilterOptions = isVoykanUi ? config.inspectionStatusesVoykan : config.inspectionStatuses;

const FormDivider = (props) => {
  const { label } = props;

  return (
    <Divider
      textAlign='center'
      sx={(theme) => ({
        width: '100%',
        alignSelf: 'center',
        justifyContent: 'center',
        marginTop: theme.spacing(1.25),
        marginBottom: theme.spacing(1.25),
      })}>
      <Typography variant='body2'>{label}</Typography>
    </Divider>
  );
};

const ChecklistInspectionFilterForm = forwardRef((props, ref) => {
  const { defaultValues, onSubmit } = props;
  const { inspectionFiltersData } = useSelector((state) => state.checklist);
  const { control, watch, register, setValue, handleSubmit } = useForm({
    defaultValues: {
      ...defaultValues,
      transport_group_id: defaultValues.transport_group_id ?? [],
      autocolon_id: defaultValues.autocolon_id ?? [],
    },
  });

  const { asyncAction: fetchFilterData, isLoading: isFetchFilterLoading } = useAsyncAction(inspectionFilterLoad);

  const fromWatch = watch('from');

  const handleChangeFromDatetime = (newValue) => {
    setValue('from', moment(newValue).format('YYYY-MM-DD HH:mm:ss'));
  };

  const handleChangeToDatetime = (newValue) => {
    setValue('to', moment(newValue).format('YYYY-MM-DD HH:mm:ss'));
  };

  useImperativeHandle(ref, () => ({
    handleFormSubmit: () => {
      handleSubmit((data) => onSubmit(data))();
    },
  }));

  useEffect(() => {
    fetchFilterData();
  }, []);

  return (
    <form>
      <FormDivider label='Временной интервал' />
      <Grid container spacing={2}>
        <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale={moment.locale('ru')}>
          <Grid item xs={6}>
            <FormControl margin='normal' variant='outlined' fullWidth>
              <Controller
                name='from'
                control={control}
                render={({ field }) => (
                  <DesktopDateTimePicker
                    label='Начало'
                    views={['day', 'hours']}
                    minDate={moment().subtract(90, 'days')}
                    maxDate={moment()}
                    inputFormat='DD.MM.YYYY HH:mm:ss'
                    value={field.value}
                    onChange={handleChangeFromDatetime}
                    renderInput={({ error, ...params }) => (
                      <TextField
                        {...params}
                        sx={{
                          svg: {
                            fontSize: '20px',
                          },
                        }}
                        {...register(field.name)}
                      />
                    )}
                  />
                )}></Controller>
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <FormControl margin='normal' variant='outlined' fullWidth>
              <Controller
                name='to'
                control={control}
                render={({ field }) => (
                  <DesktopDateTimePicker
                    label='Завершение'
                    views={['day', 'hours']}
                    minDate={fromWatch}
                    maxDate={moment()}
                    inputFormat='DD.MM.YYYY HH:mm:ss'
                    value={field.value}
                    onChange={handleChangeToDatetime}
                    renderInput={({ error, ...params }) => (
                      <TextField
                        {...params}
                        sx={{
                          svg: {
                            fontSize: '20px',
                          },
                        }}
                        {...register(field.name)}
                      />
                    )}
                  />
                )}></Controller>
            </FormControl>
          </Grid>
        </LocalizationProvider>
      </Grid>
      <FormDivider label='Название шаблона' />
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FormControl margin='normal' variant='outlined' fullWidth>
            {isFetchFilterLoading && (
              <OutlinedInput
                disabled
                placeholder='Название шаблона'
                endAdornment={
                  <InputAdornment position='end'>
                    <CircularProgress size={16} />
                  </InputAdornment>
                }
              />
            )}
            {!isFetchFilterLoading && (
              <Autocomplete
                multiple
                filterSelectedOptions
                clearText=''
                closeText=''
                noOptionsText='По данному запросу ничего не найдено'
                options={inspectionFiltersData.template}
                getOptionLabel={(option) => option.name}
                onChange={(event, options) =>
                  setValue(
                    'template_id',
                    options.map((option) => option.id)
                  )
                }
                key={inspectionFiltersData.template.filter((item) => defaultValues.template_id.includes(item.id))}
                defaultValue={inspectionFiltersData.template.filter((item) => defaultValues.template_id.includes(item.id))}
                ChipProps={{
                  sx: {
                    margin: '0!important',
                    marginRight: '4px!important',
                    height: '24px',
                    '.MuiChip-label': {
                      padding: '0 8px',
                    },
                    '.MuiChip-deleteIcon': {
                      fontSize: '16px',
                    },
                  },
                }}
                componentsProps={{
                  paper: {
                    sx: {
                      maxHeight: 200,
                      overflow: 'auto',
                      marginTop: '1px',
                      '&:before': {
                        display: 'none',
                      },
                      '.MuiAutocomplete-listbox': {
                        overflow: 'hidden',
                      },
                      '.MuiAutocomplete-option': {
                        paddingTop: '6px',
                        paddingBottom: '6px',
                        paddingLeft: '12px',
                        paddingRight: '12px',
                        fontSize: '12px',
                      },
                      '.MuiAutocomplete-noOptions': {
                        fontSize: '12px',
                      },
                    },
                  },
                }}
                renderInput={(props) => (
                  <TextField
                    {...props}
                    placeholder='Название шаблона'
                    sx={{
                      '.MuiOutlinedInput-root': {
                        paddingTop: '14px',
                        paddingBottom: '14px',
                        paddingLeft: '14px',
                      },
                      '.MuiOutlinedInput-input': {
                        padding: '0!important',
                      },
                    }}
                  />
                )}
              />
            )}
          </FormControl>
        </Grid>
      </Grid>
      <FormDivider label={isVoykanUi ? 'Статусы' : 'Статус'} />
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FormControl margin='normal' variant='outlined' fullWidth>
            <Autocomplete
              multiple
              clearText=''
              closeText=''
              noOptionsText='По данному запросу ничего не найдено'
              options={inspectionStatusFilterOptions}
              getOptionLabel={(option) => option.label}
              onChange={(event, options) =>
                setValue(
                  'status',
                  options.map((option) => option.value)
                )
              }
              key={inspectionStatusFilterOptions.filter((item) => defaultValues.status.includes(item.value))}
              defaultValue={inspectionStatusFilterOptions.filter((item) => defaultValues.status.includes(item.value))}
              ChipProps={{
                sx: {
                  margin: '0!important',
                  marginRight: '4px!important',
                  height: '24px',
                  '.MuiChip-label': {
                    padding: '0 8px',
                  },
                  '.MuiChip-deleteIcon': {
                    fontSize: '16px',
                  },
                },
              }}
              componentsProps={{
                paper: {
                  sx: {
                    maxHeight: 200,
                    overflow: 'auto',
                    marginTop: '1px',
                    '&:before': {
                      display: 'none',
                    },
                    '.MuiAutocomplete-listbox': {
                      overflow: 'hidden',
                    },
                    '.MuiAutocomplete-option': {
                      paddingTop: '6px',
                      paddingBottom: '6px',
                      paddingLeft: '12px',
                      paddingRight: '12px',
                      fontSize: '12px',
                    },
                    '.MuiAutocomplete-noOptions': {
                      fontSize: '12px',
                    },
                  },
                },
              }}
              renderInput={(props) => (
                <TextField
                  {...props}
                  placeholder='Статус'
                  sx={{
                    '.MuiOutlinedInput-root': {
                      paddingTop: '14px',
                      paddingBottom: '14px',
                      paddingLeft: '14px',
                    },
                    '.MuiOutlinedInput-input': {
                      padding: '0!important',
                    },
                  }}
                />
              )}
            />
          </FormControl>
        </Grid>
      </Grid>
      <FormDivider label='Сотрудник' />
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FormControl margin='normal' variant='outlined' fullWidth>
            {isFetchFilterLoading && (
              <OutlinedInput
                disabled
                placeholder='ФИО сотрудника'
                endAdornment={
                  <InputAdornment position='end'>
                    <CircularProgress size={16} />
                  </InputAdornment>
                }
              />
            )}
            {!isFetchFilterLoading && (
              <Autocomplete
                multiple
                filterSelectedOptions
                clearText=''
                closeText=''
                noOptionsText='По данному запросу ничего не найдено'
                options={inspectionFiltersData.employee}
                getOptionLabel={(option) => option.name}
                onChange={(event, options) =>
                  setValue(
                    'employee_id',
                    options.map((option) => option.id)
                  )
                }
                key={inspectionFiltersData.employee.filter((item) => defaultValues.employee_id.includes(item.id))}
                defaultValue={inspectionFiltersData.employee.filter((item) => defaultValues.employee_id.includes(item.id))}
                ChipProps={{
                  sx: {
                    margin: '0!important',
                    marginRight: '4px!important',
                    height: '24px',
                    '.MuiChip-label': {
                      padding: '0 8px',
                    },
                    '.MuiChip-deleteIcon': {
                      fontSize: '16px',
                    },
                  },
                }}
                componentsProps={{
                  paper: {
                    sx: {
                      maxHeight: 200,
                      overflow: 'auto',
                      marginTop: '1px',
                      '&:before': {
                        display: 'none',
                      },
                      '.MuiAutocomplete-listbox': {
                        overflow: 'hidden',
                      },
                      '.MuiAutocomplete-option': {
                        paddingTop: '6px',
                        paddingBottom: '6px',
                        paddingLeft: '12px',
                        paddingRight: '12px',
                        fontSize: '12px',
                      },
                      '.MuiAutocomplete-noOptions': {
                        fontSize: '12px',
                      },
                    },
                  },
                }}
                renderInput={(props) => (
                  <TextField
                    {...props}
                    placeholder='ФИО сотрудника'
                    sx={{
                      '.MuiOutlinedInput-root': {
                        paddingTop: '14px',
                        paddingBottom: '14px',
                        paddingLeft: '14px',
                      },
                      '.MuiOutlinedInput-input': {
                        padding: '0!important',
                      },
                    }}
                  />
                )}
              />
            )}
          </FormControl>
        </Grid>
      </Grid>
      <FormDivider label={isVoykanUi ? 'Бригада' : 'Транспортное средство'} />
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FormControl margin='normal' variant='outlined' fullWidth>
            {isFetchFilterLoading && (
              <OutlinedInput
                disabled
                placeholder={isVoykanUi ? 'Бригада' : 'Госномер'}
                endAdornment={
                  <InputAdornment position='end'>
                    <CircularProgress size={16} />
                  </InputAdornment>
                }
              />
            )}
            {!isFetchFilterLoading && (
              <Autocomplete
                multiple
                filterSelectedOptions
                clearText=''
                closeText=''
                noOptionsText='По данному запросу ничего не найдено'
                options={inspectionFiltersData.transport}
                getOptionLabel={(option) => option.number}
                onChange={(event, options) =>
                  setValue(
                    'transport_id',
                    options.map((option) => option.id)
                  )
                }
                key={inspectionFiltersData.transport.filter((item) => defaultValues.transport_id.includes(item.id))}
                defaultValue={inspectionFiltersData.transport.filter((item) => defaultValues.transport_id.includes(item.id))}
                ChipProps={{
                  sx: {
                    margin: '0!important',
                    marginRight: '4px!important',
                    height: '24px',
                    '.MuiChip-label': {
                      padding: '0 8px',
                    },
                    '.MuiChip-deleteIcon': {
                      fontSize: '16px',
                    },
                  },
                }}
                componentsProps={{
                  paper: {
                    sx: {
                      maxHeight: 200,
                      overflow: 'auto',
                      marginTop: '1px',
                      '&:before': {
                        display: 'none',
                      },
                      '.MuiAutocomplete-listbox': {
                        overflow: 'hidden',
                      },
                      '.MuiAutocomplete-option': {
                        paddingTop: '6px',
                        paddingBottom: '6px',
                        paddingLeft: '12px',
                        paddingRight: '12px',
                        fontSize: '12px',
                      },
                      '.MuiAutocomplete-noOptions': {
                        fontSize: '12px',
                      },
                    },
                  },
                }}
                renderInput={(props) => (
                  <TextField
                    {...props}
                    placeholder={isVoykanUi ? 'Бригада' : 'Госномер'}
                    sx={{
                      '.MuiOutlinedInput-root': {
                        paddingTop: '14px',
                        paddingBottom: '14px',
                        paddingLeft: '14px',
                      },
                      '.MuiOutlinedInput-input': {
                        padding: '0!important',
                      },
                    }}
                  />
                )}
              />
            )}
          </FormControl>
        </Grid>
      </Grid>
      {isVoykanUi && (
        <>
          <FormDivider label='Цех' />
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl margin='normal' variant='outlined' fullWidth>
                {isFetchFilterLoading && (
                  <OutlinedInput
                    disabled
                    placeholder='Цех'
                    endAdornment={
                      <InputAdornment position='end'>
                        <CircularProgress size={16} />
                      </InputAdornment>
                    }
                  />
                )}
                {!isFetchFilterLoading && (
                  <Autocomplete
                    multiple
                    filterSelectedOptions
                    clearText=''
                    closeText=''
                    noOptionsText='По данному запросу ничего не найдено'
                    options={inspectionFiltersData.transport_group}
                    getOptionLabel={(option) => option.name}
                    onChange={(event, options) =>
                      setValue(
                        'transport_group_id',
                        options.map((option) => option.id)
                      )
                    }
                    key={inspectionFiltersData.transport_group.filter((item) => (defaultValues.transport_group_id || []).includes(item.id))}
                    defaultValue={inspectionFiltersData.transport_group.filter((item) =>
                      (defaultValues.transport_group_id || []).includes(item.id)
                    )}
                    ChipProps={{
                      sx: {
                        margin: '0!important',
                        marginRight: '4px!important',
                        height: '24px',
                        '.MuiChip-label': {
                          padding: '0 8px',
                        },
                        '.MuiChip-deleteIcon': {
                          fontSize: '16px',
                        },
                      },
                    }}
                    componentsProps={{
                      paper: {
                        sx: {
                          maxHeight: 200,
                          overflow: 'auto',
                          marginTop: '1px',
                          '&:before': {
                            display: 'none',
                          },
                          '.MuiAutocomplete-listbox': {
                            overflow: 'hidden',
                          },
                          '.MuiAutocomplete-option': {
                            paddingTop: '6px',
                            paddingBottom: '6px',
                            paddingLeft: '12px',
                            paddingRight: '12px',
                            fontSize: '12px',
                          },
                          '.MuiAutocomplete-noOptions': {
                            fontSize: '12px',
                          },
                        },
                      },
                    }}
                    renderInput={(props) => (
                      <TextField
                        {...props}
                        placeholder='Цех'
                        sx={{
                          '.MuiOutlinedInput-root': {
                            paddingTop: '14px',
                            paddingBottom: '14px',
                            paddingLeft: '14px',
                          },
                          '.MuiOutlinedInput-input': {
                            padding: '0!important',
                          },
                        }}
                      />
                    )}
                  />
                )}
              </FormControl>
            </Grid>
          </Grid>
          <FormDivider label='Направления' />
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl margin='normal' variant='outlined' fullWidth>
                {isFetchFilterLoading && (
                  <OutlinedInput
                    disabled
                    placeholder='Направления'
                    endAdornment={
                      <InputAdornment position='end'>
                        <CircularProgress size={16} />
                      </InputAdornment>
                    }
                  />
                )}
                {!isFetchFilterLoading && (
                  <Autocomplete
                    multiple
                    filterSelectedOptions
                    clearText=''
                    closeText=''
                    noOptionsText='По данному запросу ничего не найдено'
                    options={inspectionFiltersData.autocolon}
                    getOptionLabel={(option) => option.name}
                    onChange={(event, options) =>
                      setValue(
                        'autocolon_id',
                        options.map((option) => option.id)
                      )
                    }
                    key={inspectionFiltersData.autocolon.filter((item) => (defaultValues.autocolon_id || []).includes(item.id))}
                    defaultValue={inspectionFiltersData.autocolon.filter((item) => (defaultValues.autocolon_id || []).includes(item.id))}
                    ChipProps={{
                      sx: {
                        margin: '0!important',
                        marginRight: '4px!important',
                        height: '24px',
                        '.MuiChip-label': {
                          padding: '0 8px',
                        },
                        '.MuiChip-deleteIcon': {
                          fontSize: '16px',
                        },
                      },
                    }}
                    componentsProps={{
                      paper: {
                        sx: {
                          maxHeight: 200,
                          overflow: 'auto',
                          marginTop: '1px',
                          '&:before': {
                            display: 'none',
                          },
                          '.MuiAutocomplete-listbox': {
                            overflow: 'hidden',
                          },
                          '.MuiAutocomplete-option': {
                            paddingTop: '6px',
                            paddingBottom: '6px',
                            paddingLeft: '12px',
                            paddingRight: '12px',
                            fontSize: '12px',
                          },
                          '.MuiAutocomplete-noOptions': {
                            fontSize: '12px',
                          },
                        },
                      },
                    }}
                    renderInput={(props) => (
                      <TextField
                        {...props}
                        placeholder='Направления'
                        sx={{
                          '.MuiOutlinedInput-root': {
                            paddingTop: '14px',
                            paddingBottom: '14px',
                            paddingLeft: '14px',
                          },
                          '.MuiOutlinedInput-input': {
                            padding: '0!important',
                          },
                        }}
                      />
                    )}
                  />
                )}
              </FormControl>
            </Grid>
          </Grid>
        </>
      )}
    </form>
  );
});

export default ChecklistInspectionFilterForm;
